import { useRef, useState, useCallback, useEffect } from 'react';
import { Play, Pause, Download, Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

function fmt(secs) {
  if (!secs && secs !== 0) return '0:00';
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export default function RecordingPlayer({ callSid, duration = 0, compact = false }) {
  const audioRef = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(duration || 0);

  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const ensureLoaded = useCallback(async () => {
    if (blobUrl) return blobUrl;
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get(`/voice/recording/${callSid}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setBlobUrl(url);
      return url;
    } catch (err) {
      setFailed(true);
      toast.error(err.response?.status === 404
        ? 'This call has no recording available'
        : 'Could not load the recording');
      return null;
    } finally {
      setLoading(false);
    }
  }, [blobUrl, callSid]);

  const togglePlay = async (e) => {
    e?.stopPropagation();
    const url = await ensureLoaded();
    if (!url) return;
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); return; }
    if (el.src !== url) el.src = url;
    try { await el.play(); } catch { /* user gesture already handled */ }
  };

  const handleDownload = async (e) => {
    e?.stopPropagation();
    try {
      const res = await api.get(`/voice/recording/${callSid}?download=1`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-${callSid}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const seek = (e) => {
    e.stopPropagation();
    const el = audioRef.current;
    if (!el || !total) return;
    const rect = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - rect.left) / rect.width) * total;
  };

  const pct = total ? Math.min(100, (current / total) * 100) : 0;

  return (
    <div
      className={`flex items-center gap-2 ${compact ? '' : 'w-full'}`}
      data-testid={`recording-player-${callSid}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        data-testid={`recording-play-${callSid}`}
        onClick={togglePlay}
        disabled={loading}
        title={playing ? 'Pause recording' : 'Play recording'}
        className="w-8 h-8 flex-shrink-0 rounded-full bg-[#003366] text-white hover:bg-[#004080] disabled:opacity-60 flex items-center justify-center transition-colors"
      >
        {loading
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : failed
            ? <AlertTriangle className="w-3.5 h-3.5" />
            : playing
              ? <Pause className="w-3.5 h-3.5" />
              : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>

      <div className="flex-1 min-w-[70px]">
        <div
          onClick={seek}
          className="h-1.5 bg-slate-200 rounded-full overflow-hidden cursor-pointer"
          data-testid={`recording-progress-${callSid}`}
        >
          <div className="h-full bg-[#3399FF] transition-[width] duration-150" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[10px] text-slate-400 mt-0.5 font-mono" data-testid={`recording-time-${callSid}`}>
          {fmt(current)} / {fmt(total)}
        </p>
      </div>

      <button
        data-testid={`recording-download-${callSid}`}
        onClick={handleDownload}
        title="Download recording"
        className="w-7 h-7 flex-shrink-0 rounded-full text-slate-400 hover:text-[#003366] hover:bg-slate-100 flex items-center justify-center transition-colors"
      >
        <Download className="w-3.5 h-3.5" />
      </button>

      <audio
        ref={audioRef}
        src={blobUrl || undefined}
        preload="none"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setTotal(d);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
      />
    </div>
  );
}
